import { ref, shallowRef, watchEffect } from 'vue';

import { propsFactory } from '@components/utils';

import type { SwitcherIconProps } from '../components/SwitcherIcon';
import type { TCdsIconsName } from '@central-design-system/icons';
import type { NTree } from '../interface';
import type { ShallowRef, ExtractPropTypes, PropType } from 'vue';

export const makeTreeProps = propsFactory(
  {
    /**
     * Данные дерева
     */
    treeData: {
      type: Array as PropType<NTree.IDataNode[]>,
      default: undefined
    },
    /**
     * Выбираемые узлы дерева
     */
    selectable: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Множественный выбор узлов дерева
     */
    multiple: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Добавляет checkbox перед узлами дерева
     */
    checkable: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Разрешает перетаскивание узлов дерева
     */
    draggable: {
      type: [Object, Boolean] as PropType<NTree.TDraggableConfig | boolean>,
      default: false
    },
    /**
     * (v-model) Определяет ключи выбранных узлов дерева.
     * Когда указан ключ узла дерева, который также является родительским узлом дерева, все дочерние узлы дерева будут checked;
     * и наоборот, когда указан ключ узла дерева, который является дочерним узлом дерева, его родительский узел дерева также будет checked.
     * Когда checkable и checkStrictly равны true, объект имеет свойство checked и halfChecked. Независимо от того, проверяется ли дочерний или родительский узел дерева, они не будут влиять друг на друга.
     */
    checkedKeys: {
      type: [Object, Array] as PropType<NTree.TKey[] | { checked: NTree.TKey[]; halfChecked: NTree.TKey[] }>,
      default: undefined
    },
    /**
     * (v-model) Определяет ключи раскрытых узлов дерева.
     */
    expandedKeys: {
      type: Array as PropType<NTree.TKey[]>,
      default: undefined
    },
    /**
     * (v-model) Определяет ключи выбранных узлов дерева.
     */
    selectedKeys: {
      type: Array as PropType<NTree.TKey[]>,
      default: undefined
    },
    /**
     * Автоматически раскрывать родительский узел
     */
    autoExpandParent: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Раскрывать все узлы по умолчанию
     */
    defaultExpandedKeys: {
      type: Array as PropType<NTree.TKey[]>,
      default: () => []
    },
    /**
     * Раскрывать родительские узлы по умолчанию
     */
    defaultExpandParent: {
      type: Boolean as PropType<boolean>,
      default: true
    },
    /**
     * Раскрывать все узлы по умолчанию
     */
    defaultExpandAll: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Выбранные узлы дерева по умолчанию
     */
    defaultSelectedKeys: {
      type: Array as PropType<NTree.TKey[]>,
      default: undefined
    },
    /**
     * Действие, которое будет раскрывать/скрывать узлы дерева
     */
    expandAction: {
      type: [Boolean, String] as PropType<NTree.TExpandAction>,
      default: undefined
    },
    /**
     * Выбирает только родительский узел.
     */
    checkStrictly: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Разрешает сброс на узле
     */
    allowDrop: {
      type: Function as PropType<NTree.TAllowDrop>,
      default: () => true
    },
    /**
     * Узел дерева заполняет все оставшееся горизонтальное пространство
     */
    blockNode: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Заменяет поля title, key и children в treeNode на соответствующие поля в treeData
     */
    fieldNames: {
      type: Object as PropType<NTree.IFieldNames>,
      default: undefined
    },
    /**
     * Показывает линии между узлами дерева
     */
    showLine: {
      type: [Boolean, Object] as PropType<boolean | { showLeafIcon: boolean | TCdsIconsName }>,
      default: undefined
    },
    /**
     * Показывает иконку узла дерева
     */
    showIcon: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Функция для фильтрации (выделения) узлов дерева. Когда функция возвращает true, узел дерева будет выделен.
     */
    filterTreeNode: {
      type: Function as PropType<(treeNode: NTree.IEventDataNode) => boolean>,
      default: undefined
    },
    /**
     * Загружает данные для узла дерева асинхронно
     */
    loadData: {
      type: Function as PropType<(treeNode: NTree.IEventDataNode) => Promise<any>>,
      default: undefined
    },
    /**
     * Загруженные узлы дерева. Необходимо для работы с loadData
     */
    loadedKeys: {
      type: Array as PropType<NTree.TKey[]>,
      default: () => []
    },
    /**
     * Иконка узла дерева (также доступно через slot#icon)
     */
    icon: {
      type: Function as PropType<(nodeProps: NTree.ITreeNodeAttribute) => any>,
      default: undefined
    },
    /**
     * Иконка открытия/закрытия узла дерева (также доступно через slot#switcherIcon)
     */
    switcherIcon: {
      type: Function as PropType<(nodeProps: SwitcherIconProps) => any>,
      default: undefined
    },
    /**
     * Функция анимации открытия/закрытия узла дерева
     */
    openAnimation: {
      type: Function,
      default: undefined
    },

    // Virtual List
    /**
     * Включает виртуальную прокрутку списка в дереве, по-умолчанию включено
     */
    virtual: {
      type: Boolean as PropType<boolean>,
      default: true
    },
    /**
     * Высота виртуального списка для прокрутки
     */
    height: {
      type: Number as PropType<number>,
      default: undefined
    }
  },
  'tree'
);

export interface ITreeProps extends Partial<ExtractPropTypes<ReturnType<typeof makeTreeProps>>> {
  onLoad?: (loadedKeys: NTree.TKey[], info: { event: 'load'; node: NTree.IEventDataNode }) => void;
  onDragenter?: (info: NTree.TNodeDragEventParams & { expandedKeys: NTree.TKey[] }) => void;
  onDragstart?: (info: NTree.TNodeDragEventParams) => void;
  onDragover?: (info: NTree.TNodeDragEventParams) => void;
  onDragleave?: (info: NTree.TNodeDragEventParams) => void;
  onDragend?: (info: NTree.TNodeDragEventParams) => void;
  onDrop?: (
    info: NTree.TNodeDragEventParams & {
      dragNode: NTree.IEventDataNode | null;
      dragNodesKeys: NTree.TKey[];
      dropPosition: number;
      dropToGap: boolean;
    }
  ) => void;
  onCheck?: (
    checked: { checked: NTree.TKey[]; halfChecked: NTree.TKey[] } | NTree.TKey[],
    info: NTree.ICheckInfo
  ) => void;
  onExpand?: (
    expandedKeys: NTree.TKey[],
    info: { node: NTree.IEventDataNode; expanded: boolean; nativeEvent: MouseEvent }
  ) => void;
  onSelect?: (
    selectedKeys: NTree.TKey[],
    info: {
      event: 'select';
      selected: boolean;
      node: NTree.IEventDataNode;
      selectedNodes: NTree.IDataNode[];
      nativeEvent: MouseEvent;
    }
  ) => void;
}

export function useMaxLevel<TreeDataType extends NTree.IBasicDataNode = NTree.IDataNode>(
  keyEntities: ShallowRef<Record<NTree.TKey, NTree.IDataEntity>>
) {
  const maxLevel = ref(0);
  const levelEntities = shallowRef<Map<number, Set<NTree.IDataEntity<TreeDataType>>>>();

  watchEffect(() => {
    const newLevelEntities = new Map<number, Set<NTree.IDataEntity<TreeDataType>>>();
    const keyEntitiesValue = keyEntities.value || {};

    let newMaxLevel = 0;

    for (const key in keyEntitiesValue) {
      if (Object.prototype.hasOwnProperty.call(keyEntitiesValue, key)) {
        const entity = keyEntitiesValue[key];
        const { level } = entity;

        let levelSet: Set<NTree.IDataEntity<TreeDataType>> | undefined = newLevelEntities.get(level!);

        if (!levelSet) {
          levelSet = new Set();
          newLevelEntities.set(level!, levelSet);
        }

        levelSet.add(entity as unknown as NTree.IDataEntity<TreeDataType>);

        newMaxLevel = Math.max(newMaxLevel, level!);
      }
    }

    maxLevel.value = newMaxLevel;
    levelEntities.value = newLevelEntities;
  });

  return { maxLevel, levelEntities };
}
